import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import {
  getFirestore,
  connectFirestoreEmulator,
  enableMultiTabIndexedDbPersistence,
} from 'firebase/firestore';
import {
  getAuth,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  setPersistence,
} from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
// getMessaging is removed from here to prevent premature initialization
import type { App } from 'vue';
import { pickAuthDomain } from '@/utils/authDomain';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  // Sign in on the address the Hub is opened on (hub.oovi.tw or the old web.app one).
  authDomain: pickAuthDomain(window.location.hostname, import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// Explicit persistence chain. iOS Safari PWA defaults are unreliable: ITP can
// evict IndexedDB without notice, and silent fallback to in-memory leaves users
// re-logging in every session. Prefer IndexedDB, fall back to localStorage,
// last resort logs (we never want inMemoryPersistence on a PWA — that's the
// re-login-every-launch bug).
setPersistence(auth, indexedDBLocalPersistence)
  .catch(() => setPersistence(auth, browserLocalPersistence))
  .catch((err) => {
    console.error('[firebase] Failed to set any durable auth persistence:', err);
  });
const functions = getFunctions(app, 'asia-east1');
const storage = getStorage(app);
// const messaging = getMessaging(app); // DO NOT INITIALIZE MESSAGING HERE

// Use emulators if the .env.development file specifies to do so
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  console.log('✅ Development mode: Connecting to Firebase Emulators');
  const host = 'localhost';
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8080);
  // Port overridable so a second worktree's functions emulator can serve THIS
  // frontend while another tree's stack holds the default ports (shared-machine
  // multi-session setup, 2026-07-17). Default unchanged.
  const functionsPort = Number(import.meta.env.VITE_EMULATOR_FUNCTIONS_PORT ?? 5001);
  connectFunctionsEmulator(functions, host, functionsPort);
  connectStorageEmulator(storage, host, 9199);

  // Enable persistence after emulator connection
  enableMultiTabIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.error('🔥 Firestore multi-tab persistence failed.', err);
    } else if (err.code === 'unimplemented') {
      console.warn('⚠️ Firestore persistence not supported in this browser');
    }
  });
} else {
  // Safeguard against accidental production connection in local development
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.error('CRITICAL ERROR: Connecting to PRODUCTION Firebase from localhost!');
    console.error('This is likely due to a missing or incorrect .env.development file.');
    console.error('Ensure VITE_USE_EMULATORS="true" is set in your .env.development file.');
    // You can throw an error here to halt the app completely if desired
    // throw new Error('Misconfigured local environment. Halting to prevent production data contamination.');
  }
  console.log('Production mode: Connecting to live Firebase services.');

  // Enable persistence for production
  enableMultiTabIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.error('🔥 Firestore multi-tab persistence failed.', err);
    } else if (err.code === 'unimplemented') {
      console.warn('⚠️ Firestore persistence not supported in this browser');
    }
  });

  // Initialize App Check for production
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });

  // Firebase Performance Monitoring was removed 2026-09-20. Its telemetry
  // endpoint (firebaselogging-pa) returned 503 continuously and the SDK treats
  // any HTTP response as success (no `response.ok` check in postToFlEndpoint),
  // so batches were silently dropped — the Performance dashboard sat empty for
  // the whole Aug 22–Sep 20 window while the console filled with failed POSTs.
  // Unfixed in the latest SDK (0.7.14) and not configurable. Backend timing
  // lives in Cloud Logging via ErrorHandler.logPerformance instead.
}

// Vue plugin function
export default function (vueApp: App) {
  // for use inside Vue files (Options API) through this.$firebase
  vueApp.config.globalProperties.$firebase = app;
  // for use inside Vue files (Composition API) through inject
  vueApp.provide('firebase', app);
}

// Messaging is no longer exported from here. It will be initialized on-demand.
export { app, db, auth, functions, storage };
